export type AnimToken =
  | { type: "word"; text: string }
  | { type: "tagged"; tag: string; text: string };

/**
 * Parses a sentence into animation tokens, splitting plain text by word and
 * preserving XML-style tagged segments (e.g. `<gradient>ANYTHING</gradient>`)
 * as single tokens.
 */
export function parseAnimTokens(sentence: string): AnimToken[] {
  const tokens: AnimToken[] = [];
  const tagPattern = /<(\w+)>(.*?)<\/\1>/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tagPattern.exec(sentence)) !== null) {
    if (match.index > lastIndex) {
      sentence
        .slice(lastIndex, match.index)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .forEach((word) => tokens.push({ type: "word", text: word }));
    }
    tokens.push({ type: "tagged", tag: match[1] ?? "", text: match[2] ?? "" });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < sentence.length) {
    sentence
      .slice(lastIndex)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => tokens.push({ type: "word", text: word }));
  }

  return tokens;
}

/**
 * Normalizes a snake_case or kebab-case string into a readable format.
 * Example: "social_media" → "Social media"
 *
 * @param {string} text - The text to normalize
 * @returns {string} The normalized text
 */
export function normalizeText(text: string): string {
  return text
    .replaceAll(/[_-]+/g, " ")
    .toLowerCase()
    .replaceAll(/^./g, (c) => c.toUpperCase());
}

/**
 * Collapses any run of whitespace (spaces, tabs, line breaks) into a single
 * space and trims the edges. Example: "Some title.  \n\n" -> "Some title."
 * Whitespace-only input returns an empty string.
 */
export function sanitizeText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Builds display initials from a first and last name, e.g. "John" + "Doe" -> "J D".
 * Missing names are skipped.
 */
export function getInitials(
  firstName?: string | null,
  lastName?: string | null
): string {
  const initial = (name?: string | null) =>
    name?.[0] ? name[0].toUpperCase() + " " : "";

  return (initial(firstName) + initial(lastName)).trim();
}

// converted from backend python code
export function slugify(text: string, allowTrailing = false): string {
  /**
   * Make slug string with dash pattern.
   * For example: 'Lac Viet' -> 'lac-viet'
   * @param text - The text to slugify
   * @param allowTrailing - If true, only trim leading spaces (useful for live input). If false, trim both leading and trailing spaces.
   */

  // Strip leading/trailing spaces (or only leading if allowTrailing is true)
  text = allowTrailing ? text.replaceAll(/^\s+/g, "") : text.trim();

  // Normalize and remove any non-ASCII or control characters
  text = text.normalize("NFKD").replaceAll(/[^\x20-\x7E]/g, ""); // Remove non-ASCII characters

  // Replace spaces with hyphens
  text = text.replaceAll(/\s+/g, "-");

  // Keep only alphanumeric characters and hyphens, convert to lowercase
  let result = text
    .split("")
    .filter((x) => /[a-zA-Z0-9-]/.test(x))
    .join("")
    .toLowerCase()
    .replaceAll(/-{2,}/g, "-");

  result = result.replaceAll(/^-/g, "");
  if (!allowTrailing) {
    result = result.replaceAll(/-$/g, "");
  }
  return result;
}
